import {companiesSelector, useGetAllCompaniesQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {CompanyDataTable} from "../../components/data-table/CompanyDataTable/CompanyDataTable";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {useApp} from "../../providers/RegionProvider/RegionProvider";

export type CompanyDataTablePageProps = {}

export const CompanyDataTablePageRoute = () => {
  return (
    <CompanyDataTablePage/>
  );
};

export const CompanyDataTablePage = (props: CompanyDataTablePageProps) => {

  const {regionId} = useApp()
  const {t} = useTranslation(["translation", "common"]);

  const [access, setAccess] = useState(false);

  const selectCompanies = useMemo(() => {
    return companiesSelector();
  }, []);

  const {
    companies,
    isLoading,
    error,
    refetch,
  } = useGetAllCompaniesQuery(access ? regionId : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      companies: selectCompanies(result, {regionId: regionId}),
      isLoading: result.isLoading,
      error: result.error
    })
  });

  return (
    <ProtectedPage permissions={["view", "company_view"]} isLoading={isLoading} error={error}
                   onAccess={setAccess}>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title order={4}>
                      {t("common.entity.company.plural", "Companies", {ns: "common"})}
                    </Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("common.actions.plural", "Actions", {ns: "common"})}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <CompanyDataTable
                  withBorder
                  records={companies}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isLoading}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ProtectedPage>
  )
};
